import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useScreenSize } from '@banhala/ui-kit';
import { isNotNil } from 'shared/src/utils';
import { type View } from 'react-native';
import { useInstanceId, useForegroundInterval } from 'shared/src/hooks';

const VISIBILITY_CHECK_INTERVAL = 300;

const sectionVisibilityAtom = atom<Record<string, Record<string, boolean>>>(
  {},
);

export const useIsSectionVisible = (key: string) => {
  const instanceId = useInstanceId();
  const derivedAtom = useMemo(
    () =>
      atom(get => isNotNil(get(sectionVisibilityAtom)[instanceId]?.[key])),
    [instanceId, key],
  );
  return useAtomValue(derivedAtom);
};

export const useMarkSectionVisible = () => {
  const instanceId = useInstanceId();
  const set = useSetAtom(sectionVisibilityAtom);
  return useCallback(
    (key: string) => {
      set(prev => {
        if (prev[instanceId]?.[key]) return prev;
        return {
          ...prev,
          [instanceId]: { ...prev[instanceId], [key]: true },
        };
      });
    },
    [instanceId, set],
  );
};

/**
 * 컴포넌트가 뷰포트에 진입했는지 감지하는 훅.
 * 중첩된 서브 섹션 컴포넌트에서 사용.
 *
 * 반환된 ref를 View에 연결하면,
 * 300ms 간격으로 해당 요소가 화면에 보이는지 체크하여 visibility atom을 업데이트.
 * marked 이후에는 interval이 정리됨.
 */
export const useElementViewportTracking = (
  visibilityKey: string,
  offset = 0,
) => {
  const { height: screenHeight } = useScreenSize();
  const [isMarked, setIsMarked] = useState(false);
  const isMarkedRef = useRef(false);
  const viewRef = useRef<View>(null);
  const markVisible = useMarkSectionVisible();

  const checkVisibility = useCallback(() => {
    if (isMarkedRef.current) return;
    viewRef.current?.measureInWindow((_x, y, _w, h) => {
      if (h > 0 && y < screenHeight - offset) {
        isMarkedRef.current = true;
        setIsMarked(true);
        markVisible(visibilityKey);
      }
    });
  }, [screenHeight, offset, markVisible, visibilityKey]);

  useForegroundInterval(
    checkVisibility,
    isMarked ? null : VISIBILITY_CHECK_INTERVAL,
  );

  return viewRef;
};
